/**
 * Catalog live-session listing.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Icon } from "@iconify/react";
import { Box, Grid, Tab, Tabs, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

import { BPopOver } from "components";
import BZoomGridItem from "components/BZoomGridItem";
import BZoomListItem from "components/BZoomListItem";
import { useAsterController } from "context";
import { VButton, VText } from "form";

/**
 * Lists upcoming virtual classrooms from the catalog.
 */
const ExploreVirtual = () => {
  const { t } = useTranslation("common");

  const [controller] = useAsterController();
  const { mobile, meetEvents, loadedDashboard } = controller;

  const [tab, setTab] = useState(0);
  const [term, setTerm] = useState("");

  const showList = useMemo(() => {
    const tmp = term.toLowerCase();
    return (meetEvents.data || []).filter((v) => v.topic?.toLowerCase()?.includes(tmp));
  }, [meetEvents.data, term]);

  const handleSetTabValue = (_, newValue) => setTab(newValue);

  useEffect(() => mobile && setTab(0), [mobile]);

  return (
    <>
      <Box className="pt-8 pb-5" id="v-user-title">
        <Grid container spacing={0}>
          <Grid item xs={12} md={4} className="w-full md:flex hidden justify-center md:justify-start">
            <VText className="text-2xl">{meetEvents?.total || 0}</VText>&nbsp;
            <VText color="secondary" className="text-2xl">
              {t("Contents in total")}
            </VText>
          </Grid>
          <Grid item sm={12} md={8} className="flex justify-center lg:justify-end flex-wrap items-center">
            <VButton variant="text" color="secondary" className="text-sm normal-case" onClick={() => setTerm("")} disabled={!term}>
              <Icon icon="akar-icons:circle-check" />
              &nbsp;{t("All status")}
            </VButton>
            <BPopOver
              className="ml-2"
              autoClose={false}
              trigger={
                <VButton variant="text" color="secondary" className="text-sm normal-case">
                  <Icon icon="ant-design:filter-filled" />
                  &nbsp;{t("Filter")}
                </VButton>
              }
            >
              <TextField className="w-64" size="small" value={term} onChange={(e) => setTerm(e.target.value)} />
            </BPopOver>
            <Box className="h-8 ml-2 v-tab-switch w-min invisible lg:visible">
              <Tabs orientation="horizontal" value={tab} onChange={handleSetTabValue}>
                <Tab icon={<Icon className="mr-0" icon="akar-icons:grid" />} />
                <Tab icon={<Icon className="mr-0" icon="ci:list-checklist" />} />
              </Tabs>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {tab === 0 && (
        <Box>
          <Grid container spacing={3}>
            {loadedDashboard
              ? showList.map((session) => (
                  <Grid key={session.id} item xs={12} sm={6} md={4} xl={3}>
                    <BZoomGridItem session={session} disabled={true} />
                  </Grid>
                ))
              : [...Array(10).keys()].map((v) => (
                  <Grid key={v} item xs={12} sm={6} md={4} xl={3}>
                    <Box className="rounded-md border border-solid border-gray-200 dark:border-gray-500 p-4">
                      <Skeleton className="aspect-[9/5] mb-4" />
                      <Skeleton count={4} className="mt-2" />
                      <Skeleton className="h-8 mt-4" />
                    </Box>
                  </Grid>
                ))}
          </Grid>
        </Box>
      )}
      {tab === 1 && (
        <Box>
          <Grid container spacing={3}>
            {loadedDashboard
              ? showList.map((session) => (
                  <Grid key={session.id} item xs={12}>
                    <BZoomListItem session={session} disabled={true} />
                  </Grid>
                ))
              : [...Array(3).keys()].map((v) => (
                  <Grid key={v} item xs={12}>
                    <Skeleton className="w-full h-32" />
                  </Grid>
                ))}
          </Grid>
        </Box>
      )}
    </>
  );
};

export default ExploreVirtual;
